import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../../app/auth-context';
import { ApiError, api } from '../../lib/api';

/**
 * Login em duas etapas. A primeira responde `totp_required` quando a conta
 * tem 2FA; so entao pedimos o codigo — antes disso a tela nao revela se o
 * e-mail existe nem se tem segunda etapa.
 */

const credentialsSchema = z.object({
  email: z.string().trim().min(1, 'Informe o e-mail.').email('E-mail invalido.'),
  password: z.string().min(1, 'Informe a senha.'),
});

type Credentials = z.infer<typeof credentialsSchema>;

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState<'credentials' | 'totp'>('credentials');
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });

  const submitCredentials = form.handleSubmit(async (values) => {
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.login(values);

      if (result.status === 'totp_required') {
        setCredentials(values);
        setStage('totp');
        return;
      }

      signIn(result.user, result.accessToken);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  });

  const submitSecondFactor = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!credentials) return;

    setError(null);
    setSubmitting(true);
    try {
      const result = await api.login({
        ...credentials,
        ...(useRecovery
          ? { recoveryCode: code.trim().toUpperCase() }
          : { totpCode: code.replace(/\D/g, '') }),
      });

      if (result.status !== 'authenticated') {
        setError('Nao foi possivel concluir a verificacao.');
        return;
      }

      signIn(result.user, result.accessToken);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(messageFor(caught));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      <main className="auth-card">
        {stage === 'credentials' ? (
          <>
            <h1>Entrar</h1>
            <p className="subtitle">Controle financeiro pessoal.</p>

            {error && (
              <div className="alert error" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={submitCredentials} noValidate>
              <div className="field">
                <label htmlFor="email">E-mail</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  aria-invalid={Boolean(form.formState.errors.email)}
                  {...form.register('email')}
                />
                {form.formState.errors.email && (
                  <span className="field-error">{form.formState.errors.email.message}</span>
                )}
              </div>

              <div className="field">
                <label htmlFor="password">Senha</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={Boolean(form.formState.errors.password)}
                  {...form.register('password')}
                />
                {form.formState.errors.password && (
                  <span className="field-error">{form.formState.errors.password.message}</span>
                )}
              </div>

              <button className="btn block" type="submit" disabled={submitting}>
                {submitting ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1>Verificacao</h1>
            <p className="subtitle">
              {useRecovery
                ? 'Digite um dos codigos de recuperacao guardados na ativacao.'
                : 'Digite o codigo de 6 digitos do seu aplicativo autenticador.'}
            </p>

            {error && (
              <div className="alert error" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={submitSecondFactor}>
              <div className="field">
                <label htmlFor="code">{useRecovery ? 'Codigo de recuperacao' : 'Codigo'}</label>
                <input
                  id="code"
                  className={useRecovery ? 'mono' : 'code-input'}
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={useRecovery ? 14 : 6}
                  placeholder={useRecovery ? 'XXXX-XXXX-XXXX' : '000000'}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>

              <button className="btn block" type="submit" disabled={submitting || code.length < 6}>
                {submitting ? 'Verificando...' : 'Confirmar'}
              </button>
            </form>

            <div className="row" style={{ marginTop: 'var(--space-4)' }}>
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setUseRecovery((value) => !value);
                  setCode('');
                  setError(null);
                }}
              >
                {useRecovery ? 'Usar o aplicativo' : 'Perdi o aplicativo'}
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setStage('credentials');
                  setCode('');
                  setError(null);
                }}
              >
                Voltar
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'TOTP_NOT_ENABLED') {
      return 'Esta conta ainda precisa configurar a verificacao em duas etapas.';
    }
    return error.message;
  }
  return 'Nao foi possivel conectar ao servidor.';
}
